import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConfirmDetectWebAppsBodyDto } from '@terra/shared-contracts-external-network';
import type { WebAppDraftStrategyType } from '@terra/shared-schemas-orchestrator';
import { externalNetworkInventoryApi } from '../../clients/tester-client/external-network-inventory-api';
import { TARGETS_QUERY_KEY } from '../useTargets';
import { EXTERNAL_NETWORK_DETAIL_KEY } from './useSurfaceSettings';

export const EXTERNAL_NETWORK_WEB_APP_CLUSTERS_KEY =
  'external-network-web-app-clusters';
export const EXTERNAL_NETWORK_WEB_APP_PREVIEW_KEY =
  'external-network-web-app-preview';

export function useExternalNetworkWebAppClusters({
  surfaceId,
  enabled = true,
}: {
  surfaceId: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [EXTERNAL_NETWORK_WEB_APP_CLUSTERS_KEY, surfaceId],
    queryFn: () => externalNetworkInventoryApi.listWebAppClusters(surfaceId),
    enabled,
  });
}

export function usePreviewExternalNetworkWebAppDrafts({
  surfaceId,
  strategy,
  enabled,
}: {
  surfaceId: string;
  strategy?: WebAppDraftStrategyType;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [EXTERNAL_NETWORK_WEB_APP_PREVIEW_KEY, surfaceId, strategy],
    queryFn: () =>
      externalNetworkInventoryApi.previewWebAppDrafts({ surfaceId, strategy }),
    enabled,
  });
}

export function useConfirmExternalNetworkDetectWebApps(surfaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ConfirmDetectWebAppsBodyDto) =>
      externalNetworkInventoryApi.confirmDetectWebApps({ surfaceId, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_DETAIL_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_WEB_APP_CLUSTERS_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_WEB_APP_PREVIEW_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] });
    },
  });
}
